import { createRef, useEffect, useState } from 'react';
import Page from '../..';
import Table, { TableHeader } from '../../../Table';
import useStore from '../../../Hooks/useStore';
import SVG from '../../../SVG';
import { useNavigate } from 'react-router-dom';
import {
  formatBRL,
  formatBRLForList,
  formatCentsForList,
} from '../../../../store/masks';
import {
  getFilterColumn,
  getSortColumn,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';
import { useSnackbar } from 'notistack';

const PageTicketOfflinePrices = () => {
  const navigate = useNavigate();
  const [getters, setters] = useStore();
  const {
    product: { getProduct },
    user: { getUser },
  } = getters;
  const {
    product: {
      setProduct,
      findFilteredOfflineProducts,
      autocompleteOfflineProducts,
    },
  } = setters;
  const selectedFilalId = getUser('selectedFilialId');
  const ref = createRef<HTMLDivElement>();
  const { enqueueSnackbar } = useSnackbar();
  const searchFilter = getProduct('filters.offline.filter');
  const setSearchFilter = (filter: SearchFilter) =>
    setProduct({
      field: 'filters.offline.filter',
      value: filter,
    });

  const products = getProduct('filters.offline.data.data');

  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    const value = e.target.value;
    let filter = setFilterColumn(field, value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getAutocompleteItems = (
    field: keyof ProductState['autocompletes']['offline'],
  ) =>
    getProduct(`autocompletes.offline.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof ProductState['autocompletes']['offline'],
  ) => {
    autocompleteOfflineProducts(
      getProduct(`autocompletes.offline.${field}.filter`),
    )
      .then((data) => {
        setProduct({
          field: `autocompletes.offline.${field}.data`,
          value: data,
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar os produtos', {
          variant: 'error',
        });
      });
  };

  const setAutocompleteFilterField =
    (field: keyof ProductState['autocompletes']['offline']) => (e) => {
      setProduct({
        field: `autocompletes.offline.${field}.filter.value`,
        value: e.target.value,
      });
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  const [loadingProducts, setLoadingProducts] = useState(
    products.map(() => false),
  );

  const setLoadingProduct = (index: number, value: boolean) => {
    setLoadingProducts((loadingProducts) =>
      loadingProducts.map((v, i) => (i === index ? value : v)),
    );
  };

  const handleSelectProduct = (index: number) => {};

  const items = products.map((product, index) => ({
    code: product.code,
    ean: product.ean,
    resume: product.resume,
    regularPrice: product.price.regularPrice,
    promotionalPrice: product.price.promotionalPrice,
    regularPriceOffline: product.offlinePrice.regularPrice,
    promotionalPriceOffline: product.offlinePrice.promotionalPrice,
    createdAt: product.offlinePrice.createdAt,
    updatedAt: product.offlinePrice.updatedAt,
  }));

  const total = getProduct('filters.offline.data.meta.total');

  const handleFilter = () => {
    findFilteredOfflineProducts(getProduct('filters.offline.filter'))
      .then((data) => {
        setProduct({
          field: 'filters.offline.data',
          value: data,
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar os produtos', {
          variant: 'error',
        });
      });
  };

  let searchTimerId: NodeJS.Timeout;

  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(searchTimerId);
      searchTimerId = setTimeout(() => {
        handleFilter();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(searchTimerId);
    };
  }, [searchFilter, selectedFilalId]);

  const headers: TableHeader[] = [
    {
      text: 'Código',
      value: 'code',
      width: 'auto',
      filter: {
        value: getFilterField('code'),
        onChange: setField('code'),
        items: getAutocompleteItems('code'),
        handleItems: () => setAutocompleteItems('code'),
        label: 'Código do produto',
      },
      sort: {
        value: getSortField('code'),
        onChange: setSortField('code'),
      },
      tooltip: {
        translateX: 0,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('code', '', searchFilter));
        setProduct({
          field: `autocompletes.offline.code.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Código barras',
      value: 'ean',
      width: '0.5fr',
      filter: {
        value: getFilterField('ean'),
        onChange: setField('ean'),
        items: getAutocompleteItems('ean'),
        handleItems: () => setAutocompleteItems('ean'),
        label: 'Código de barras',
      },
      sort: {
        value: getSortField('ean'),
        onChange: setSortField('ean'),
      },
      tooltip: {
        translateX: 0.25,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('ean', '', searchFilter));
        setProduct({
          field: `autocompletes.offline.ean.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Descrição',
      value: 'resume',
      width: '1fr',
      filter: {
        value: getFilterField('resume'),
        onChange: setField('resume'),
        items: getAutocompleteItems('resume'),
        handleItems: () => setAutocompleteItems('resume'),
        label: 'Descrição',
      },
      sort: {
        value: getSortField('resume'),
        onChange: setSortField('resume'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('resume', '', searchFilter));
        setProduct({
          field: `autocompletes.offline.resume.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Preço regular',
      value: 'regularPrice',
      width: '0.5fr',
      mask: ({ regularPrice }) => formatBRL(regularPrice),
      filter: {
        mask: formatBRLForList,
        unmask: formatCentsForList,
        value: getFilterField('regularPrice'),
        onChange: setField('regularPrice'),
        items: getAutocompleteItems('regularPrice'),
        handleItems: () => setAutocompleteItems('regularPrice'),
        label: 'Preço Regular',
      },
      sort: {
        value: getSortField('regularPrice'),
        onChange: setSortField('regularPrice'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('regularPrice', '', searchFilter));
        setProduct({
          field: `autocompletes.offline.regularPrice.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Preço promocional',
      value: 'promotionalPrice',
      width: '0.5fr',
      mask: ({ promotionalPrice }) => formatBRL(promotionalPrice),
      filter: {
        mask: formatBRLForList,
        unmask: formatCentsForList,
        value: getFilterField('promotionalPrice'),
        onChange: setField('promotionalPrice'),
        items: getAutocompleteItems('promotionalPrice'),
        handleItems: () => setAutocompleteItems('promotionalPrice'),
        label: 'Preço Promocional',
      },
      sort: {
        value: getSortField('promotionalPrice'),
        onChange: setSortField('promotionalPrice'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('promotionalPrice', '', searchFilter));
        setProduct({
          field: `autocompletes.offline.promotionalPrice.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Preço regular offline',
      value: 'regularPriceOffline',
      width: '0.5fr',
      mask: ({ regularPriceOffline }) => formatBRL(regularPriceOffline),
      filter: {
        mask: formatBRLForList,
        unmask: formatCentsForList,
        value: getFilterField('regularPriceOffline'),
        onChange: setField('regularPriceOffline'),
        items: getAutocompleteItems('regularPriceOffline'),
        handleItems: () => setAutocompleteItems('regularPriceOffline'),
        label: 'Preço Regular',
      },
      sort: {
        value: getSortField('regularPriceOffline'),
        onChange: setSortField('regularPriceOffline'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(
          setFilterColumn('regularPriceOffline', '', searchFilter),
        );
        setProduct({
          field: `autocompletes.offline.regularPriceOffline.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Preço promocional Offline',
      value: 'promotionalPriceOffline',
      width: '0.5fr',
      mask: ({ promotionalPriceOffline }) => formatBRL(promotionalPriceOffline),
      filter: {
        mask: formatBRLForList,
        unmask: formatCentsForList,
        value: getFilterField('promotionalPriceOffline'),
        onChange: setField('promotionalPriceOffline'),
        items: getAutocompleteItems('promotionalPriceOffline'),
        handleItems: () => setAutocompleteItems('promotionalPriceOffline'),
        label: 'Preço Promocional',
      },
      sort: {
        value: getSortField('promotionalPriceOffline'),
        onChange: setSortField('promotionalPriceOffline'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(
          setFilterColumn('promotionalPriceOffline', '', searchFilter),
        );
        setProduct({
          field: `autocompletes.offline.promotionalPriceOffline.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Atualizado em',
      value: 'updatedAt',
      width: '0.75fr',
      mask: ({ updatedAt }) => new Date(updatedAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('updatedAt'),
        onChange: setSortField('updatedAt'),
      },
      interval: [
        {
          value: getFilterField('updatedAt', 'GEQ'),
          onChange: setField('updatedAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('updatedAt', 'LT'),
          onChange: setField('updatedAt', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 1,
      },
      onClear: () => {
        let filter = setFilterColumn('updatedAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('updatedAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
  ];

  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  return (
    <Page className="page-banner">
      <div
        ref={ref}
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 190px)',
          maxHeight: 'calc(100vh - 190px)',
          margin: '0 2rem',
          backgroundColor: '#ececec',
          borderRadius: '10px',
          overflow: 'hidden',
          padding: '2rem',
        }}
      >
        <Table headers={headers} items={items}>
          <Table.Control
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalOfItems={total}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </Table>
      </div>
    </Page>
  );
};

export default PageTicketOfflinePrices;
